import { exportSheetToExcel, exportSheetToPDF, safeFilePart } from './sheetExport';
import { formatISODate } from './dates';
import { formatBoxes43, formatRatio, roundBoxes43, summarizeHarvestSales } from './harvestSales';

// Same palette as the on-screen DATOS DE COSECHA sheet (HarvestFormPage / SalesFormPage.css).
const HEADER_GREY = 'F4F6F3';
const MANUAL_GREEN = 'E2F0D9';

// Observaciones in sheet order. Keys match HARVEST_DEFECT_FIELDS in backend/core/models.py.
const DEFECTS = [
  ['mutant', 'MUTANTE'],
  ['over_grade', 'SOBRE GRADO'],
  ['altered', 'ALTERADO'],
  ['poor', 'POBRE'],
  ['erwinia', 'ERWIÑIA'],
  ['under_grade', 'BAJO GRADO'],
  ['sooty_mold', 'FUMAGINA'],
  ['chlorotic', 'CLORÓTICOS'],
  ['mealybug', 'COCHINILLA'],
  ['fallen', 'CAÍDO'],
  ['cream', 'CREMA'],
  ['virosis', 'VIROSIS'],
  ['red_spot', 'MANCHA ROJA'],
  ['explosive', 'EXPLOSIVOS'],
];

// Blends a ribbon color with white so its label stays readable.
function lightenHex(hex, whiteAmount = 0.7) {
  if (!hex) return undefined;
  const clean = hex.replace('#', '');
  const mix = (i) => Math.round(parseInt(clean.substring(i, i + 2), 16) * (1 - whiteAmount) + 255 * whiteAmount);
  return [0, 2, 4].map((i) => mix(i).toString(16).padStart(2, '0')).join('').toUpperCase();
}

const intRow = (label, value, fill) => ({
  label,
  text: value == null ? '' : String(value),
  number: value == null ? undefined : value,
  fill,
});

// Rows in the exact order of the Detalles de Cosecha form.
export function harvestSheet(h) {
  const s = summarizeHarvestSales(h.sales_detail || []);
  const ribbons = h.ribbons || [];
  const totalBoxes43 = h.total_boxes_43 != null
    ? Number(h.total_boxes_43)
    : s.boxes.reduce((sum, b) => sum + (b.value43 || 0), 0);
  const ribbonTotal = ribbons.reduce((sum, rb) => sum + (rb.quantity || 0), 0);
  return {
    name: 'Cosecha',
    fileBase: `Cosecha_${safeFilePart(h.farm_name || s.farm, 'hacienda')}_${h.date}_${h.id}`,
    header: 'DATOS DE COSECHA',
    headerColor: HEADER_GREY,
    boldLabels: true,
    rows: [
      { label: 'FECHA', text: formatISODate(h.date), date: h.date },
      intRow('SEMANA', h.week),
      { label: 'DIA', text: h.day },
      s.ids.length === 1 ? intRow('ID DE VENTA', s.ids[0]) : { label: 'ID DE VENTA', text: s.ids.join(', ') },
      { label: 'EMPRESA', text: h.farm_company_name || s.company },
      { label: 'HACIENDA', text: h.farm_name || s.farm, fill: MANUAL_GREEN },
      { label: 'EXPORTADORA', text: s.exporters },
      { label: 'MARCA DE CAJA', text: s.brands },

      { section: true, label: 'OBSERVACIONES', fill: HEADER_GREY },
      ...DEFECTS.map(([key, label]) => intRow(label, h[key], MANUAL_GREEN)),
      intRow('RACIMOS RECHAZADOS', h.bunches_rejected),
      intRow('RACIMOS COSECHADOS', h.bunches_harvested),
      intRow('RACIMOS PROCESADOS', h.bunches_processed),
      {
        label: 'RATIO',
        text: h.ratio == null ? '' : formatRatio(h.ratio),
        number: h.ratio == null ? undefined : Number(h.ratio),
        decimal: true,
      },
      // Per sale: cajas, peso de caja, cajas (43lbs).
      ...s.boxes.flatMap((b, i) => {
        const w = s.fruitWeights[i];
        return [
          intRow(b.label, b.value, MANUAL_GREEN),
          { label: w.label, text: w.text, number: w.value == null ? undefined : w.value, decimal: true },
          { label: b.label43, text: formatBoxes43(b.value43), number: roundBoxes43(b.value43) ?? undefined },
        ];
      }),
      {
        label: 'TOTAL CAJAS (43LBS)',
        text: formatBoxes43(totalBoxes43),
        number: roundBoxes43(totalBoxes43) ?? undefined,
      },

      { section: true, label: 'TOTAL DE CINTA', fill: HEADER_GREY },
      ...ribbons.map((rb, i) => ({
        ...intRow(`${i + 1}. ${rb.ribbon_color || 'CINTA'} · EDAD ${rb.age ?? '—'}`, rb.quantity, MANUAL_GREEN),
        labelFill: lightenHex(rb.ribbon_hex_color),
      })),
      intRow('TOTAL CINTA', ribbonTotal),

      { label: 'LOTES COSECHADOS', text: h.lots_harvested || '', fill: MANUAL_GREEN },
      { label: 'LOTES FLORIADOS', text: h.lots_flowered || '', fill: MANUAL_GREEN },
    ],
  };
}

export function exportHarvestToExcel(harvest) {
  return exportSheetToExcel(harvestSheet(harvest));
}

export function exportHarvestToPDF(harvest) {
  exportSheetToPDF(harvestSheet(harvest));
}
